import { DownloadIcon, FileTextIcon, HashIcon, LayersIcon, MapPinIcon, ZapIcon } from "lucide-react";

import type { Policy, PolicyProcessingState } from "../../../state/policies";
import { Badge } from "../../ui/badge";
import { formatCount } from "./documentTime";

function Stat({
  icon: Icon,
  label,
  value,
}: {
  icon?: typeof LayersIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="text-right">
      <div className="flex items-center justify-end gap-1.5 text-xs text-muted-foreground">
        {Icon ? <Icon className="size-3" /> : null}
        {label}
      </div>
      <div className="mt-0.5 font-mono text-sm font-medium tabular-nums">{value}</div>
    </div>
  );
}

/**
 * The document itself: a file reference you can open, its identity, and what
 * ingestion made of it.
 *
 * The file button downloads through the server rather than linking to the
 * storage origin — a policy stays behind the tenant guard instead of living
 * on a public, link-secret URL. Counts prefer the live run's tally when one is
 * running, so the numbers move while the work happens, and fall back to what
 * the document recorded.
 */
export function DocumentInfoCard({
  policy,
  processing,
  jurisdictionNames,
  onDownload,
}: {
  policy: Policy;
  processing: PolicyProcessingState | null;
  jurisdictionNames: ReadonlyMap<string, string>;
  onDownload: () => void;
}) {
  const chunks = processing?.stats?.chunksProcessed ?? policy.chunkCount;

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5">
      <div className="flex flex-wrap items-start gap-5">
        <button
          type="button"
          onClick={onDownload}
          className="group flex cursor-pointer items-center gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3 text-left transition-colors hover:border-primary/30 hover:bg-muted/70"
        >
          <span className="flex size-9 items-center justify-center rounded-md bg-destructive/10 text-destructive">
            <FileTextIcon className="size-4.5" />
          </span>
          <span className="flex flex-col">
            <span className="line-clamp-1 max-w-[200px] text-sm font-medium leading-tight">
              {policy.filename}
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              Open PDF
              <DownloadIcon className="size-3" />
            </span>
          </span>
        </button>

        <div className="flex min-w-0 flex-1 flex-wrap items-start justify-between gap-6">
          <div className="min-w-0 space-y-3 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <HashIcon className="size-3.5 shrink-0" />
              <span className="truncate font-mono text-xs">{policy.id}</span>
            </div>
            <div className="flex items-start gap-2">
              <MapPinIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
              {policy.jurisdictionSlugs.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {policy.jurisdictionSlugs.map((slug) => (
                    <Badge
                      key={slug}
                      variant="secondary"
                      className="rounded-full px-2 py-0 text-[10px] font-medium uppercase tracking-wider"
                    >
                      {jurisdictionNames.get(slug) ?? slug}
                    </Badge>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">
                  Not tagged — regulatory monitoring cannot match changes to it.
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-5">
            <Stat label="Pages" value={formatCount(policy.pageCount)} />
            <div className="h-8 w-px bg-border/60" />
            <Stat icon={LayersIcon} label="Chunks" value={formatCount(chunks)} />
            <div className="h-8 w-px bg-border/60" />
            <Stat icon={ZapIcon} label="Tokens" value={formatCount(policy.embeddingTokens)} />
          </div>
        </div>
      </div>
    </div>
  );
}
